/**
 * The warn-not-block dialog (DESIGN.md §21): a warning the operator may
 * proceed past, with a safe default.
 *
 * Two buttons, in DOM order: a red "OK, I understand" (proceed) then a green
 * "I'll fix it" (the recommended path). Green sits rightmost on `sm+` and on
 * top on mobile (the footer's `flex-col-reverse`). Radix focuses the Cancel
 * button on open, so "I'll fix it" has the focus, and Escape (or any other
 * dismissal) means "I'll fix it". Colour is never the only signal: both
 * buttons carry their full label.
 *
 * Open it only from an inline surface, never from inside another dialog
 * (DESIGN.md §10).
 *
 * A warning whose two answers are better named by the action itself (the
 * replace notice: Upload or Cancel) passes `proceedLabel` and `fixLabel`.
 *
 * `safeAnswer` says which answer the warning recommends, and so which one is
 * green and in the primary spot. `'fix'` (the default, every caller before
 * the Platen size save warning) is the layout above. `'proceed'` is for a
 * warning whose normal answer is to go ahead and which only informs: "OK, I
 * understand" turns green and primary (DOM-last), "I'll fix it" red. A caller
 * passing `'proceed'` must spell out any harm in the body itself (the Platen
 * size warning's red line above the buttons), because colour no longer
 * warns. Focus and Escape stay on "I'll fix it" in both modes, so a stray
 * Enter or Escape never goes ahead. Labels never change with it.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { buttonVariants } from '@/components/ui/button-variants';
import { cn } from '@/lib/utils';

export interface WarnNotBlockDialogProps {
  open: boolean;
  title: ReactNode;
  description: ReactNode;
  /** Optional body under the description (for example a list of what is missing). */
  children?: ReactNode;
  /** Width and extra classes for the content, for example `max-w-md`. */
  contentClassName?: string;
  /**
   * "OK, I understand": go ahead anyway. Absent: there is no going ahead
   * (the server would refuse it), so only the safe button shows.
   */
  onProceed?: () => void;
  /** "I'll fix it", Escape, or any other dismissal: take the safe path. */
  onFix: () => void;
  /** The red button's label, translated. Default `common:warnNotBlock.proceed`. */
  proceedLabel?: string;
  /** The green (safe, focused) button's label, translated. Default `common:warnNotBlock.fix`. */
  fixLabel?: string;
  /** Where focus goes on close; absent (or null): back where it was, as Radix does. */
  returnFocus?: () => HTMLElement | null;
  /**
   * The recommended answer, green and in the primary spot. `'fix'` (default):
   * fix first. `'proceed'`: going ahead is normal; the body must then state
   * any harm in words. Focus and Escape stay on the fix answer either way.
   */
  safeAnswer?: 'fix' | 'proceed';
}

// emerald-700 (about 5.5:1 with white) keeps the recommended button at AA for
// its 14px label; emerald-600 was about 3.8:1.
const RECOMMENDED_CLASSES = 'border-0 bg-emerald-700 text-white hover:bg-emerald-800 hover:text-white';

export function WarnNotBlockDialog({
  open,
  title,
  description,
  children,
  contentClassName,
  onProceed,
  onFix,
  proceedLabel,
  fixLabel,
  returnFocus,
  safeAnswer = 'fix',
}: WarnNotBlockDialogProps) {
  const { t } = useTranslation('common');
  // Radix closes the dialog after either button's onClick, so the close
  // handler must know a button already answered; otherwise the close is an
  // Escape and means "I'll fix it".
  const answeredRef = useRef(false);
  useEffect(() => {
    if (open) answeredRef.current = false;
  }, [open]);

  // 'proceed' only applies when there is a proceed answer; with no onProceed
  // the one fix button keeps its default green.
  const proceedIsRecommended = safeAnswer === 'proceed' && onProceed !== undefined;
  // Radix focuses the Cancel (fix) button on open in both modes, so focus and
  // Escape never go ahead. Recommended = green and DOM-last (primary).
  const proceedButton = onProceed && (
    <AlertDialogAction
      className={proceedIsRecommended ? RECOMMENDED_CLASSES : buttonVariants({ variant: 'destructive' })}
      onClick={() => {
        answeredRef.current = true;
        onProceed();
      }}
    >
      {proceedLabel ?? t('common:warnNotBlock.proceed')}
    </AlertDialogAction>
  );
  const fixButton = (
    <AlertDialogCancel
      className={
        proceedIsRecommended
          ? cn(buttonVariants({ variant: 'destructive' }), 'border-0 hover:text-destructive-foreground')
          : RECOMMENDED_CLASSES
      }
      onClick={() => {
        answeredRef.current = true;
        onFix();
      }}
    >
      {fixLabel ?? t('common:warnNotBlock.fix')}
    </AlertDialogCancel>
  );

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (next) return;
        if (answeredRef.current) return;
        answeredRef.current = true;
        onFix();
      }}
    >
      <AlertDialogContent
        className={contentClassName}
        onCloseAutoFocus={(e) => {
          const target = returnFocus?.();
          if (target) {
            e.preventDefault();
            target.focus({ preventScroll: true });
          }
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-muted-foreground">{description}</div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        {children}
        <AlertDialogFooter>
          {proceedIsRecommended ? (
            <>
              {fixButton}
              {proceedButton}
            </>
          ) : (
            <>
              {proceedButton}
              {fixButton}
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
